import { ExternalLink, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  PortalDsAlert,
  PortalDsBadge,
  PortalDsButton,
  PortalDsSectionHeader,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { LogEntryDetail } from '../types/log-monitoring.types';

type Props = {
  entry: LogEntryDetail | undefined;
  isLoading: boolean;
  isActioning: boolean;
  onIgnore: () => void;
  onMarkFixed: () => void;
  onCreateLinearIssue: () => void;
  onRestore: () => void;
  onClose: () => void;
};

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export function LogEntryDetailSheet({
  entry,
  isLoading,
  isActioning,
  onIgnore,
  onMarkFixed,
  onCreateLinearIssue,
  onRestore,
  onClose,
}: Props) {
  if (!entry && !isLoading) {
    return null;
  }

  const canRestore = entry?.status === 'ignored' || entry?.status === 'fixed';
  const canAction = entry && !['ignored', 'fixed', 'linear_linked'].includes(entry.status);
  const hasLinear = Boolean(entry?.linear_issue_id);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
      <div
        className={cn(
          portalDs.card,
          'flex h-full w-full max-w-xl flex-col overflow-hidden rounded-none border-l shadow-xl',
        )}
      >
        <div className={cn(portalDs.cardPadding, 'border-b border-[#EDEEF2]')}>
          <PortalDsSectionHeader
            overline="Log entry"
            title={entry?.llm_summary ?? 'Loading…'}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-[#5E6278]">
              <Loader2 className="mr-2 size-5 animate-spin" />
              Loading entry…
            </div>
          )}

          {entry && !isLoading && (
            <div className="flex flex-col gap-6">
              <div className="flex flex-wrap gap-2">
                <PortalDsBadge variant="gray">{entry.severity}</PortalDsBadge>
                <PortalDsBadge variant="gray">{entry.category}</PortalDsBadge>
                <PortalDsBadge variant="gray">{entry.status}</PortalDsBadge>
                {entry.is_actionable && (
                  <PortalDsBadge variant="blue">Actionable</PortalDsBadge>
                )}
              </div>

              <section className="flex flex-col gap-2">
                <h3 className={portalDs.headingH3}>Analysis</h3>
                <p className={portalDs.bodySm}>{entry.llm_description || 'No description.'}</p>
                <p className={portalDs.bodySm}>
                  <strong>Root cause:</strong>
                  {' '}
                  {entry.root_cause_hypothesis || '—'}
                </p>
                <p className={portalDs.bodySm}>
                  <strong>Recommended action:</strong>
                  {' '}
                  {entry.recommended_action || '—'}
                </p>
              </section>

              <section className="flex flex-col gap-2">
                <h3 className={portalDs.headingH3}>Occurrences</h3>
                <p className={portalDs.bodySm}>
                  Seen
                  {' '}
                  {entry.occurrence_count}
                  x · last
                  {' '}
                  {formatDistanceToNow(new Date(entry.last_seen_at), { addSuffix: true })}
                </p>
                {entry.keyword_matches?.length > 0 && (
                  <p className={portalDs.caption}>
                    Keywords:
                    {' '}
                    {entry.keyword_matches.join(', ')}
                  </p>
                )}
              </section>

              <section className="flex flex-col gap-2">
                <h3 className={portalDs.headingH3}>Raw message</h3>
                <pre className="overflow-x-auto rounded-lg bg-[#F7F8FA] p-3 font-mono text-xs text-[#3D4155]">
                  {entry.raw_message}
                </pre>
              </section>

              {entry.sample_log_entries?.length > 0 && (
                <section className="flex flex-col gap-2">
                  <h3 className={portalDs.headingH3}>Sample occurrences</h3>
                  <pre className="overflow-x-auto rounded-lg bg-[#F7F8FA] p-3 font-mono text-xs text-[#3D4155]">
                    {entry.sample_log_entries.join('\n')}
                  </pre>
                </section>
              )}

              <div className="flex flex-wrap gap-2">
                <a
                  href={entry.gcp_log_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(portalDs.link, 'inline-flex items-center gap-1')}
                >
                  GCP Logs Explorer
                  <ExternalLink className="size-3.5" />
                </a>
                {entry.linear_issue_url && (
                  <a
                    href={entry.linear_issue_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(portalDs.link, 'inline-flex items-center gap-1')}
                  >
                    {entry.linear_issue_id}
                    <ExternalLink className="size-3.5" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {entry && (
          <div className={cn(portalDs.cardPadding, 'flex flex-wrap gap-2 border-t border-[#EDEEF2]')}>
            <PortalDsButton variant="ghost" size="sm" onClick={onClose}>
              Close
            </PortalDsButton>
            {canAction && (
              <>
                <PortalDsButton
                  variant="secondary"
                  size="sm"
                  disabled={isActioning}
                  onClick={onIgnore}
                >
                  Ignore
                </PortalDsButton>
                <PortalDsButton
                  variant="secondary"
                  size="sm"
                  disabled={isActioning}
                  onClick={onMarkFixed}
                >
                  Mark fixed
                </PortalDsButton>
                <PortalDsButton
                  variant="accent"
                  size="sm"
                  disabled={isActioning || hasLinear}
                  onClick={onCreateLinearIssue}
                >
                  Create Linear issue
                </PortalDsButton>
              </>
            )}
            {canRestore && (
              <PortalDsButton
                variant="secondary"
                size="sm"
                disabled={isActioning}
                onClick={onRestore}
              >
                Restore
              </PortalDsButton>
            )}
          </div>
        )}

        {isActioning && (
          <PortalDsAlert variant="info" className="m-4">
            Updating entry…
          </PortalDsAlert>
        )}
      </div>
    </div>
  );
}
